import { ExtraToolDefinition, ExtraToolAction } from '../types/epub';
import { Dispatch, Action } from 'redux';
import { PageAction, Publication, getDefaultRendition } from '../../../..';
import { getGlobalFrameStore } from '../services/FrameStore';
import {
  PagesState,
  makePopupPanelID,
  TabInfoLinearPages,
  tabFocusSelf,
  TabType,
} from '../reducers/pages';
import { openPopup, moveToPage, selectTab } from '../actions/PageAction';
import { getTitle, getFaviconPath } from './renditions';
import { getReflowableRendition } from '../utils/epub';

export const getContentExtraToolsForCurrentPage = (
  pages: PagesState,
  publication: Publication,
): ReadonlyArray<string> => {
  if (publication === undefined) {
    throw new Error('Unexpected state.');
  }
  const tab = pages.tabs[pages.activeTab];
  if (tab.focus !== tabFocusSelf) {
    const popup = pages.popups[tab.focus];
    const item = getDefaultRendition(publication).itemIndices[popup.path].extraTools;
    if (item === undefined) {
      throw new Error('Popup item is not found.');
    }

    return item;
  }

  switch (tab.type) {
    case TabType.LinearPages: {
      const item = getDefaultRendition(publication).linearItems[tab.currentPage];
      if (item === undefined) {
        throw new Error('LinearPage item is not found.');
      }

      return item.extraTools;
    }
    case TabType.ExtraPage: {
      const item = getDefaultRendition(publication).itemIndices[tab.path];
      if (item === undefined) {
        throw new Error('ExtraPage item is not found.');
      }

      return item.extraTools;
    }
    case TabType.ReflowablePages: {
      const item = getReflowableRendition(publication).linearItems[tab.currentPage];
      if (item === undefined) {
        throw new Error('Reflowable item is not found.');
      }

      return item.extraTools;
    }
    case TabType.GeneratedContent: {
      return [];
    }
  }
};

export const createActionForExtraTool = (
  pages: PagesState,
  publication: Publication,
  definition: ExtraToolDefinition,
  dispatch: Dispatch<Action>,
): (() => void) => {
  switch (definition.action) {
    case ExtraToolAction.GoToPage: {
      return () => {
        // FIXME: 紙面タブを指すのに `0` を使っている

        const linearPagesTab = (pages.tabs[0] as TabInfoLinearPages);

        if (linearPagesTab.currentPage !== definition.pageIndex) {
          // 紙面タブ内をページ移動
          moveToPage(0, definition.pageIndex, dispatch);
        }

        if (pages.activeTab !== 0) {
          // 紙面タブを表示していなかったら紙面タブへ切り替え
          dispatch(selectTab(0));
        }
      };
    }
    case ExtraToolAction.OpenURL: {
      return () => {
        window.open(definition.url);
      };
    }
    case ExtraToolAction.OpenTab: {
      return () => {
        const path = definition.path;
        const title = getTitle(publication.renditions, path);
        const favicon = getFaviconPath(publication.renditions, path, publication.resourceDir);
        dispatch(PageAction.openNewTab(path, title, favicon, true));
      };
    }
    case ExtraToolAction.OpenPopup: {
      return () => {
        const path = definition.path;
        const title = getTitle(publication.renditions, path);
        const parent = pages.tabs[pages.activeTab].id;
        const [id, zIndex] = makePopupPanelID(pages, parent, definition.path);

        const defValues = {
          modal: true, resizable: true, titlebar: true, frame: true, collapsed: false,
        };

        try {
          dispatch(openPopup({
            ...defValues,
            parent,
            id,
            zIndex,
            path,
            title,
            ...definition.data,
          }));
        } catch (error) {
          alert(error.message);
        }
      };
    }
    case ExtraToolAction.SendMessage: {
      return () => {
        getGlobalFrameStore().broadcastContentMessage(definition.data);
      };
    }
  }
};
